import dayjs from 'dayjs';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { apiGet } from '../../services/_common';
import { IUser } from '../../atoms';
import IssueGuide from '../IssueGuide';
import SkeletonTable from '../SkeletonTable';
import { ERROR } from '../../_constants';
import styled from 'styled-components';

const Root = styled.div`
  // 상황판일 경우에 해당 스타일 적용
  &:not(.safetyViolationInfo) {
    height: 100%;
    position: relative;
    > .widget-header {
      width: 100%;
      padding: 0.75rem 1.5rem;
      > .widget-title {
        height: 2rem;
        > span {
          color: var(--widget-text-primary);
          font-weight: var(--font-semibold);
          font-size: var(--widget-font-size-title-sm);
        }
      }
      > .widget-subTitle {
        font-size: 0.875rem;
        line-height: 1rem;
        color: var(--text-secondary);
      }
    }
    > .widget-body {
      flex-grow: 1;
      width: 100%;
      padding: 0 1.5rem;
      margin-bottom: 1rem;

      user-select: none;
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      > div {
        gap: 0.75rem;
        > div:nth-child(1) {
          width: 3rem;
          height: 3rem;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 0.5rem;
          .material-symbols-rounded {
            font-variation-settings: 'FILL' 1, 'wght' 600, 'GRAD' 200, 'opsz' 40;
            font-size: 1.5rem;
          }
        }
        > div.orange {
          color: var(--widget-status-yellow);
        }
        > div.green {
          background-color: var(--tonal-green);
        }
        > div.blue {
          background-color: var(--tonal-blue);
        }
        > div:nth-child(2) {
          display: flex;
          flex-direction: column;
          font-size: 0.875rem;
          font-weight: 600;
          line-height: 1.25rem;
          color: var(--text-secondary);
          gap: 0.25rem;
          > div {
            font-size: 1.25rem;
            span {
              font-size: 1rem;
              font-weight: var(--font-semibold);
            }
          }
        }
      }
      > div.infoBox {
        gap: 0.5rem;
        font-size: 0.75rem;
        > div:nth-child(2) {
          gap: 0;
          span {
            margin-right: 0.125rem;
          }
        }
      }
    }
    .widget-body.safety {
      gap: 0.5rem;
      > div {
        border-radius: 0.25rem;
        display: flex;
        flex-direction: column;
        .orange {
          color: var(--widget-status-yellow);
        }
        .red {
          color: var(--widget-status-red);
        }
        > div.flex-column {
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          gap: 0.25rem;
          > span.text-small {
            font-size: var(--widget-font-size-xs);
            color: var(--widget-text-secondary);
          }
        }
        > div.text-area {
          > div > span {
            font-size: var(--widget-font-size-3xl);
            font-weight: var(--font-extrabold);
            padding-right: 0.25rem;
          }
        }
        > div:nth-child(1) {
          width: 100%;
          text-align: left;
          height: 100%;
          span {
            font-size: 3rem;
          }
        }
        > div:nth-child(2) {
          width: 100%;
          align-items: flex-end;
          gap: 0;
          height: 100%;
          justify-content: flex-end;
          padding-bottom: 0.75rem;
          span {
            font-size: 1.5rem;
          }
        }
      }
    }
  }
`;

// 안전 위반 현황
export const SafetyViolationInfo = ({ name, userInfo, refetchInterval }: { name: string; userInfo: IUser; refetchInterval?: number }) => {
  const isSiteDashboard = refetchInterval !== undefined; // 상황판여부
  const { hCd, sCd } = userInfo;
  const { t } = useTranslation();
  const [yesterday, setYesterday] = useState(dayjs().subtract(1, 'day'));

  const { data, isLoading, isError, refetch } = useQuery(['safetyViolationGet', userInfo], () => fetchData(), {
    enabled: !!hCd && !!sCd && sCd !== '00000',
    ...(isSiteDashboard && { refetchInterval }), // 30분
    onSettled: () => {
      setYesterday(dayjs().subtract(1, 'day'));
    },
  });

  const fetchData = async () => {
    try {
      const res = await apiGet({ path: '/safe/pointblackcontents', req: { hCd, sCd } });
      return { pointCount: res.data.data[0].pointCount, blackCount: res.data.data[0].blackCount };
    } catch (error) {
      console.error(error);
      throw new Error('error');
    }
  };

  const onClickRefresh = () => {
    refetch();
  };

  const loading = isLoading || !data;
  if (isError) return <IssueGuide text={ERROR} isImg={false} />;

  return (
    <Root className={!isSiteDashboard ? 'safetyViolationInfo' : undefined}>
      <div className='widget-header'>
        {!isSiteDashboard ? (
          <>
            <div className='widget-title flex-between'>
              <span>{t(name)}</span>
              <div className='refresh-btn' onClick={onClickRefresh} role='presentation'>
                <span className='material-symbols-rounded'>refresh</span>
              </div>
            </div>
            <div className='widget-subTitle'>
              {yesterday?.format(`MM월 DD일 dddd`)} ({t('전일')})
            </div>
          </>
        ) : (
          <div className='widget-title flex-between'>
            <span>
              {t(name)} ({t('전일')})
            </span>
          </div>
        )}
      </div>
      {loading ? (
        <SkeletonTable rows={1} />
      ) : (
        <div className='widget-body safety'>
          {isSiteDashboard ? (
            <>
              <div className='box'>
                <div className='flex-column orange'>
                  <span className='material-symbols-rounded'>warning</span>
                  <span className='text-small'>{t('안전 지적')}</span>
                </div>
                <div className='text-area orange'>
                  <div>
                    <span>{data?.pointCount ?? '0'}</span>
                    {t('명')}
                  </div>
                </div>
              </div>
              <div className='box'>
                <div className='flex-column red'>
                  <span className='material-symbols-rounded'>block</span>
                  <span className='text-small'>{t('출입 금지')}</span>
                </div>
                <div className='text-area red'>
                  <div>
                    <span>{data?.blackCount ?? '0'}</span>
                    {t('명')}
                  </div>
                </div>
              </div>
            </>
          ) : (
            <>
              <div className='box flex-basic'>
                <div className='orange'>
                  <span className='material-symbols-rounded'>warning</span>
                </div>
                <div>
                  <div>
                    <span>{data?.pointCount ?? '0'} </span>
                    {t('명')}
                  </div>
                  {t('안전 지적')}
                </div>
              </div>
              <div className='box flex-basic'>
                <div className='red'>
                  <span className='material-symbols-rounded'>block</span>
                </div>
                <div>
                  <div>
                    <span>{data?.blackCount ?? '0'} </span>
                    {t('명')}
                  </div>
                  {t('출입 금지')}
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </Root>
  );
};
